import React, { useState, useEffect } from 'react'
import { useConfig } from 'msp-integrations'
import { transformRegionToMap } from '@utils/pageUtils'
import Button from '@atoms/Button/Button'
import SlideItem, { buildTopRegion } from './SlideItem/SlideItem'
import { SlideItemPDDataType } from './SlideItem/SlideItem.types'
import * as S from './SliderCarousel.styles'
import * as T from './SliderCarousel.types'

const TILE_SIZE = '(100%)' // 324px + 14px spacing
const DESKTOP_MIN_WIDTH_PX = 1024

/**
 * Render arrow icon for recommendation carousel
 */
function getArrowIcon(
  cbAction: () => void,
  shouldDisplay = false,
  sliderStyle: string,
  position = 'left'
): JSX.Element {
  const icons = {
    left: (
      <Button
        onClick={() => cbAction()}
        type={'tertiary'}
        iconName={'ArrowLeft'}
        strokeColor={'black'}
        className={!shouldDisplay ? 'opacity-0' : S.SliderArrowIconColor}
      />
    ),
    right: (
      <Button
        onClick={() => cbAction()}
        type={'tertiary'}
        iconName={'ArrowRight'}
        strokeColor={'black'}
        className={!shouldDisplay ? 'opacity-0' : S.SliderArrowIconColor}
      />
    )
  }

  const icon = icons[position]

  return <>{icon}</>
}

/**
 * Display Carousel slide status indicator
 */
function getIndicators(
  currentIndex: number,
  totalSlideItems: number,
  handleIndicatorClick: (indicatorIndex: number) => void,
  sliderStyle: string
): JSX.Element {
  return (
    <div className={S.SliderCarouselIndicatorWrapper}>
      {Array.from({ length: totalSlideItems }).map((_, indicatorIndex) => {
        const isActive = indicatorIndex + 1 === currentIndex
        const indicatorClass = [
          S.SliderCarouselIndicatorItemWrapper,
          S.SliderCarouselForegroundStyle[sliderStyle],
          isActive ? 'h-[4px]' : ''
        ]

        return (
          <div
            key={indicatorIndex + 1}
            onClick={() => handleIndicatorClick(indicatorIndex + 1)}
            className={S.SliderCarouselIndicatorItem}
          >
            <div className={indicatorClass.join(' ')} />
          </div>
        )
      })}
    </div>
  )
}

const SliderCarousel = ({ data, regions }: T.SliderCarouselPDDataType) => {
  const areas = transformRegionToMap(regions)
  const {
    sliderStyle = 'dark',
    transitionDelay = 500,
    customCssClass = ''
  } = data
  const {
    theme: {
      font: { fontVariant: font }
    }
  } = useConfig()
  const slideItems = areas?.get('slideItems')?.components || []
  const totalSlideItems = slideItems.length
  const [tileDisplayIndex, setTileDisplayIndex] = useState<number>(1)
  const [isDesktop, setIsDesktop] = useState<boolean>(false)
  const [showNextArrow, setShowNextArrow] = useState<boolean>(false)
  const [showPrevArrow, setShowPrevArrow] = useState<boolean>(false)
  const mainWrapperCssClasses = [
    S.SliderCarouselWrapper,
    S.SliderCarouselBackgroundStyle[sliderStyle],
    customCssClass
  ]
  const slidesWrapperCssClasses = [
    S.SliderCarouselItemsWrapper,
    `duration-${transitionDelay}`
  ]

  const slideNext = () => {
    if (tileDisplayIndex + 1 <= totalSlideItems) {
      setTileDisplayIndex(tileDisplayIndex + 1)
    }
  }

  const slidePrev = () => {
    if (tileDisplayIndex - 1 > 0) {
      setTileDisplayIndex(tileDisplayIndex - 1)
    }
  }

  const handleIndicatorClick = (indicatorIndex: number) => {
    setTileDisplayIndex(indicatorIndex)
  }

  useEffect(() => {
    function handleResize() {
      if (window?.innerWidth >= DESKTOP_MIN_WIDTH_PX) setTileDisplayIndex(1)
      setIsDesktop(window?.innerWidth >= DESKTOP_MIN_WIDTH_PX)
    }

    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (tileDisplayIndex < totalSlideItems) {
      setShowNextArrow(totalSlideItems > 1)
    } else {
      setShowNextArrow(false)
    }

    if (tileDisplayIndex <= 1) {
      setShowPrevArrow(false)
    } else {
      setShowPrevArrow(totalSlideItems > 1)
    }
  }, [tileDisplayIndex])

  /**
   * Build Slide Item Tile component
   */
  function buildSlideItem(slideItem: SlideItemPDDataType): JSX.Element {
    return (
      <SlideItem
        key={`slide-item-${slideItem.id}`}
        data={{ sliderStyle: sliderStyle, ...slideItem.data }}
        regions={slideItem.regions}
      />
    )
  }

  const [firstSlide] = slideItems

  const firstSlideRegions = transformRegionToMap(firstSlide?.regions || [])
  const firstSlideTopRegion =
    firstSlideRegions?.get('slideTopRegion')?.components || null

  return (
    <div className={mainWrapperCssClasses.join(' ')}>
      <div className={S.SliderTextContentWrapper}>
        {firstSlideTopRegion &&
          buildTopRegion(firstSlideTopRegion, sliderStyle, font, true)}
      </div>
      <div
        className={slidesWrapperCssClasses.join(' ')}
        style={{
          transform: `translateX(calc(${TILE_SIZE} * ${-(
            tileDisplayIndex - 1
          )}))`
        }}
      >
        {totalSlideItems > 0 && slideItems.map(buildSlideItem)}
      </div>
      <div className={S.SliderCarouselControlsWrapper}>
        {!isDesktop && getArrowIcon(slidePrev, showPrevArrow, sliderStyle)}
        {!isDesktop &&
          getArrowIcon(slideNext, showNextArrow, sliderStyle, 'right')}
      </div>
      <div className={S.SliderControlsWrapper}>
        {!isDesktop &&
          getIndicators(
            tileDisplayIndex,
            totalSlideItems,
            handleIndicatorClick,
            sliderStyle
          )}
      </div>
    </div>
  )
}

export default SliderCarousel
